// Star-map layout: pure, renderer-agnostic geometry. No React, no DOM.
//
// TWO layout entry points:
//   - computeLayout(albums)        — GENRE mode. One star per album, two-tier
//                                    categories/sub-genres (described below).
//   - computeNebulaLayout(consts)  — BPM / KEY modes. One hazy NEBULA per
//                                    constellation (a bpm range or a key): a
//                                    seeded decorative star scatter + glow +
//                                    label + song count. NOT one star per song.
//                                    Takes the ordered constellations from
//                                    groupSongs() (./grouping). See bottom of file.
//
// computeLayout (GENRE) produces a StarMapLayout from a set of albums. Two-tier:
//   - TIER 1 (default): constellations = TOP CATEGORIES (from categorize()).
//     Every album is bucketed into one of the 14 categories or the catch-all
//     'Other' (so the ~55% ungenred albums never disappear). Album stars are
//     NOT clickable (the constellation is the click target / drill-in).
//   - TIER 2: constellations = SUB-GENRES. Global tier 2 emits one constellation
//     per (category, subgenre) pair across ALL albums; a focused tier-2 drill-in
//     filters to a single category and emits one constellation per its subgenres.
//     Album stars ARE clickable (-> solar system).
//
// In every mode:
//   - Vertical position = year (newer higher within the box).
//   - Horizontal position = seeded pseudo-random (unique shape per constellation).
//   - relaxX collision relaxation, box/shelf packing, deterministic per album id.
//
// The geometry is intentionally {x, y, r} only so a future 3D / animated renderer
// can consume the same data (adding z / time) without touching this module.
import type { AlbumItem } from '../types/model';
import type {
  Constellation,
  Nebula,
  NebulaLayout,
  NebulaStar,
  Planet,
  SolarSystem,
  Star,
  StarMapLayout,
  Tier,
} from '../types/starmap';
import { fnv1a, seededRng } from '../lib/prng';
import {
  CATEGORY_NAMES,
  SUBGENRES_BY_CATEGORY,
  categorize,
} from './constellationMap';
import type { Constellation as GroupConstellation } from './grouping';
import { computeSolarSystem } from './solarSystem';

// Re-export so callers can import either entry point.
export { computeSolarSystem };
export type { SolarSystem, Planet };

// ---- tuning constants ----
const CELL = 46; // star diameter incl. gap (px)
const STAR_R_MIN = 16;
const STAR_R_MAX = 20;
const BOX_PAD = 56; // padding between constellation boxes
const BOX_INNER_PAD = 28; // inner padding (keeps stars off the box edge / label)
const LABEL_H = 26; // headroom for the genre label above the box content
const BAND_GAP = 64; // vertical gap between shelf bands
const SCENE_MARGIN = 80;
const MIN_DIST = CELL * 0.92; // collision threshold
const RELAX_ITERS = 8;

interface BoxSpec {
  /** Stable grouping key + testid suffix (category on tier1, subgenre on tier2). */
  genre: string;
  /** Human display label. */
  label: string;
  /** Tier-1 category this constellation belongs to (always set). */
  category: string;
  /** Tier-2 sub-genre (only set when tier === 2). */
  subgenre?: string;
  /** Display/priority order index (lower = earlier); used as the pack tie-break. */
  order: number;
  albums: AlbumItem[];
  w: number;
  h: number;
  // assigned during packing:
  x: number;
  y: number;
}

/** Internal: one logical bucket of albums + its constellation metadata. */
interface Group {
  genre: string;
  label: string;
  category: string;
  subgenre?: string;
  order: number;
  albums: AlbumItem[];
}

/** Deterministic star radius (stable per album). */
function starRadius(albumId: string): number {
  const t = seededRng('r:' + albumId)();
  return STAR_R_MIN + t * (STAR_R_MAX - STAR_R_MIN);
}

/** Map a year onto [0,1] where 1 = newest. Unknown year -> 0 (bottom). */
function yearFrac(year: number | undefined, minYear: number, maxYear: number): number {
  if (year == null || !isFinite(year)) return 0;
  if (maxYear === minYear) return 0.5;
  const f = (year - minYear) / (maxYear - minYear);
  return Math.max(0, Math.min(1, f));
}

/**
 * Bucket albums into constellation groups for the requested tier/focus.
 *
 *  - tier 1: one group per category (CATEGORY_NAMES display order, Other last).
 *  - tier 2 global: one group per (category, subgenre) across all albums.
 *  - tier 2 focused: only `focusCategory` albums, one group per its subgenres.
 *
 * `order` is the display/priority index used as the deterministic pack tie-break
 * so two tiers over the same album set still produce stable, meaningful order.
 */
function groupAlbums(
  albums: AlbumItem[],
  tier: Tier,
  focusCategory?: string,
): Group[] {
  const groups = new Map<string, Group>();

  // Precompute ordering indices.
  const categoryOrder = new Map<string, number>();
  CATEGORY_NAMES.forEach((name, i) => categoryOrder.set(name, i));

  for (const a of albums) {
    const { category, subgenre } = categorize(a.genre);

    if (tier === 1) {
      const key = category;
      let g = groups.get(key);
      if (!g) {
        g = {
          genre: category,
          label: category,
          category,
          subgenre: undefined,
          order: categoryOrder.get(category) ?? CATEGORY_NAMES.length,
          albums: [],
        };
        groups.set(key, g);
      }
      g.albums.push(a);
      continue;
    }

    // tier === 2
    if (focusCategory != null && category !== focusCategory) continue;

    // Group by (category, subgenre). The constellation key/label is the
    // subgenre name; in a focused view subgenre names are unique to the
    // focused category, and globally subgenres are namespaced under category.
    const key = category + ' ' + subgenre;
    let g = groups.get(key);
    if (!g) {
      const subOrder = (SUBGENRES_BY_CATEGORY[category] ?? []).indexOf(subgenre);
      const catIdx = categoryOrder.get(category) ?? CATEGORY_NAMES.length;
      // Order: category block first, then subgenre order within it.
      const order = catIdx * 100 + (subOrder >= 0 ? subOrder : 99);
      g = {
        genre: subgenre,
        label: subgenre,
        category,
        subgenre,
        order,
        albums: [],
      };
      groups.set(key, g);
    }
    g.albums.push(a);
  }

  return [...groups.values()];
}

/**
 * GENRE-mode album layout (two-tier). BPM/KEY modes do NOT come through here —
 * they are song-grouped nebulae produced by computeNebulaLayout() below.
 */
export function computeLayout(
  albums: AlbumItem[],
  opts?: {
    tier: 1 | 2;
    focusCategory?: string;
  },
): StarMapLayout {
  const tier: Tier = opts?.tier === 2 ? 2 : 1;
  // focusCategory only meaningful on genre tier 2.
  const focusCategory =
    tier === 2 && opts?.focusCategory != null ? opts.focusCategory : undefined;

  // Bucket first so the hash reflects ONLY the albums actually laid out
  // (a focused tier-2 over a filtered set hashes that filtered set).
  const groups = groupAlbums(albums, tier, focusCategory);

  const laidOut: AlbumItem[] = [];
  for (const g of groups) for (const a of g.albums) laidOut.push(a);

  const albumSetHash = fnv1a(
    laidOut
      .map((a) => a.id)
      .sort()
      .join(','),
  ).toString(16);

  // Global year range (drives vertical mapping inside every box). Computed over
  // the albums actually laid out.
  let minYear = Infinity;
  let maxYear = -Infinity;
  for (const a of laidOut) {
    if (a.year != null && isFinite(a.year)) {
      if (a.year < minYear) minYear = a.year;
      if (a.year > maxYear) maxYear = a.year;
    }
  }
  if (!isFinite(minYear)) {
    minYear = 0;
    maxYear = 0;
  }

  // Constellation boxes, sized ~ ceil(sqrt(count)) cells, sorted by member count
  // desc for stable placement (largest first); ties broken by display order then
  // grouping key so the same (album set + tier + focus) is identical every time.
  const boxes: BoxSpec[] = groups
    .map((grp) => {
      const count = grp.albums.length;
      const cells = Math.max(1, Math.ceil(Math.sqrt(count)));
      // Expand width first if a box is too dense (give the relaxer room on X).
      const wCells = count > cells * cells - cells ? cells + 1 : cells;
      const w = wCells * CELL + BOX_INNER_PAD * 2;
      const h = cells * CELL + BOX_INNER_PAD * 2 + LABEL_H;
      return {
        genre: grp.genre,
        label: grp.label,
        category: grp.category,
        subgenre: grp.subgenre,
        order: grp.order,
        albums: grp.albums,
        w,
        h,
        x: 0,
        y: 0,
      };
    })
    .sort(
      (a, b) =>
        b.albums.length - a.albums.length ||
        a.order - b.order ||
        a.genre.localeCompare(b.genre),
    );

  // Shelf-pack boxes left->right, wrapping to the next band. Band width grows
  // with the album count so very large libraries stay roughly square.
  const totalCells = laidOut.length;
  const bandWidth = Math.max(
    640,
    Math.ceil(Math.sqrt(totalCells)) * CELL * 1.6 + SCENE_MARGIN * 2,
  );

  let cursorX = SCENE_MARGIN;
  let bandTop = SCENE_MARGIN;
  let bandHeight = 0;
  let sceneRight = 0;
  for (const box of boxes) {
    if (cursorX > SCENE_MARGIN && cursorX + box.w > bandWidth - SCENE_MARGIN) {
      // wrap to next band
      cursorX = SCENE_MARGIN;
      bandTop += bandHeight + BAND_GAP;
      bandHeight = 0;
    }
    box.x = cursorX;
    box.y = bandTop;
    cursorX += box.w + BOX_PAD;
    bandHeight = Math.max(bandHeight, box.h);
    sceneRight = Math.max(sceneRight, box.x + box.w);
  }

  const width = Math.max(bandWidth, sceneRight + SCENE_MARGIN);
  const height = bandTop + bandHeight + SCENE_MARGIN;

  // Stars are clickable only on tier 2 (tier 1 = constellation is the target).
  const clickable = tier === 2;

  // Place stars within each box, then relax collisions on X.
  const stars: Star[] = [];
  const constellations: Constellation[] = [];

  for (const box of boxes) {
    const boxLeft = box.x + BOX_INNER_PAD;
    const boxTop = box.y + BOX_INNER_PAD + LABEL_H;
    const boxInnerW = box.w - BOX_INNER_PAD * 2;
    const boxInnerH = box.h - BOX_INNER_PAD * 2 - LABEL_H;

    const local: Star[] = box.albums.map((a) => {
      const r = starRadius(a.id);
      const x = boxLeft + seededRng('x:' + a.id)() * boxInnerW;
      const y = boxTop + (1 - yearFrac(a.year, minYear, maxYear)) * boxInnerH;
      const cat = categorize(a.genre);
      return {
        albumId: a.id,
        x,
        y,
        r,
        coverArtKey: a.coverArtKey,
        artist: a.artist,
        name: a.name,
        year: a.year,
        category: cat.category,
        subgenre: cat.subgenre,
        clickable,
      };
    });

    relaxX(local, boxLeft, boxLeft + boxInnerW);

    // Constellation polyline order: by year (then id) so it reads chronologically.
    const ordered = [...local].sort(
      (s1, s2) =>
        (s1.year ?? -Infinity) - (s2.year ?? -Infinity) ||
        s1.albumId.localeCompare(s2.albumId),
    );

    constellations.push({
      genre: box.genre,
      label: box.label,
      x: box.x,
      y: box.y,
      w: box.w,
      h: box.h,
      starIds: ordered.map((s) => s.albumId),
      tier,
      category: box.category,
      subgenre: tier === 2 ? box.subgenre : undefined,
    });

    for (const s of local) stars.push(s);
  }

  return {
    stars,
    constellations,
    width,
    height,
    minYear,
    maxYear,
    albumSetHash,
    tier,
    focusCategory,
  };
}

/**
 * Deterministic collision relaxation: nudge overlapping stars apart on X only
 * (preserving each star's Y, which encodes its year). Uses a fixed grid bucket so
 * cost stays ~O(n) and the result is identical across reloads.
 */
function relaxX(stars: Star[], minX: number, maxX: number): void {
  if (stars.length < 2) return;
  const cell = MIN_DIST;
  for (let iter = 0; iter < RELAX_ITERS; iter++) {
    const grid = new Map<string, number[]>();
    const keyOf = (x: number, y: number) =>
      Math.floor(x / cell) + ':' + Math.floor(y / cell);
    for (let i = 0; i < stars.length; i++) {
      const k = keyOf(stars[i].x, stars[i].y);
      const arr = grid.get(k);
      if (arr) arr.push(i);
      else grid.set(k, [i]);
    }
    for (let i = 0; i < stars.length; i++) {
      const a = stars[i];
      const gx = Math.floor(a.x / cell);
      const gy = Math.floor(a.y / cell);
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const neigh = grid.get(gx + dx + ':' + (gy + dy));
          if (!neigh) continue;
          for (const j of neigh) {
            if (j <= i) continue;
            const b = stars[j];
            const ddx = b.x - a.x;
            const ddy = b.y - a.y;
            const dist = Math.hypot(ddx, ddy);
            if (dist >= MIN_DIST || dist === 0) {
              if (dist === 0) {
                // Identical spot: deterministic tie-break by id.
                const push = a.albumId < b.albumId ? -1 : 1;
                a.x -= push;
                b.x += push;
              }
              continue;
            }
            const overlap = (MIN_DIST - dist) / 2;
            // Move on X only; direction follows existing X delta (or id tie-break).
            const dir = ddx === 0 ? (a.albumId < b.albumId ? -1 : 1) : Math.sign(ddx);
            a.x -= dir * overlap;
            b.x += dir * overlap;
          }
        }
      }
    }
    // Clamp back into the box width.
    for (const s of stars) {
      if (s.x < minX) s.x = minX;
      else if (s.x > maxX) s.x = maxX;
    }
  }
}

// ===========================================================================
// NEBULA layout (BPM / KEY modes)
// ===========================================================================
//
// Each constellation (from groupSongs) becomes a hazy nebula: a soft glow of
// radius r, holding a SEEDED decorative scatter of small faint stars (NOT 1:1
// with songs), plus a label + song count. Nebulae are packed into bands/grid,
// mirroring the genre box packing, so the scene reads like the genre map. Fully
// deterministic: every nebula is seeded by its constellation id, so the same
// constellation set always produces the identical scene across reloads.

// ---- nebula tuning constants ----
const NEBULA_R_MIN = 70; // glow radius for the smallest constellation
const NEBULA_R_PER_SQRT = 14; // extra glow radius per sqrt(songCount)
const NEBULA_R_MAX = 220; // clamp so a huge bucket can't dominate the scene
const NEBULA_GAP = 56; // padding between nebula bounding boxes
const NEBULA_LABEL_H = 34; // headroom above the glow for the label + count
const NEBULA_STAR_MIN = 15; // fewest decorative scatter stars per nebula
const NEBULA_STAR_MAX = 30; // most decorative scatter stars per nebula
const NEBULA_STAR_R_MIN = 1.2; // faint scatter star radius (px)
const NEBULA_STAR_R_MAX = 3.2;

/** Glow radius for a constellation, scaling with sqrt(songCount) and clamped. */
function nebulaRadius(songCount: number): number {
  const r = NEBULA_R_MIN + Math.sqrt(Math.max(0, songCount)) * NEBULA_R_PER_SQRT;
  return Math.min(NEBULA_R_MAX, r);
}

/**
 * Seeded decorative scatter for one nebula: ~15–30 faint stars distributed
 * within the glow radius around (cx, cy). Uses a sqrt-radius distribution so the
 * cloud is denser toward the center (reads like a hazy core), and a single PRNG
 * stream seeded by the constellation id so it is stable across reloads and unique
 * per constellation. Returned coordinates are ABSOLUTE (scene) coordinates.
 */
function nebulaScatter(id: string, cx: number, cy: number, r: number): NebulaStar[] {
  const rng = seededRng('nebula:' + id);
  const span = NEBULA_STAR_MAX - NEBULA_STAR_MIN;
  const count = NEBULA_STAR_MIN + Math.floor(rng() * (span + 1));
  const stars: NebulaStar[] = [];
  for (let i = 0; i < count; i++) {
    const angle = rng() * Math.PI * 2;
    // sqrt keeps the density roughly uniform-to-center; * 0.92 keeps stars just
    // inside the glow edge so they read as "within" the nebula.
    const dist = Math.sqrt(rng()) * r * 0.92;
    const sr = NEBULA_STAR_R_MIN + rng() * (NEBULA_STAR_R_MAX - NEBULA_STAR_R_MIN);
    stars.push({
      x: cx + Math.cos(angle) * dist,
      y: cy + Math.sin(angle) * dist,
      r: sr,
    });
  }
  return stars;
}

/**
 * Lay out BPM/KEY constellations as nebulae. Input is the ORDERED list from
 * groupSongs (display order preserved). Each nebula's glow radius scales with
 * sqrt(songCount); nebulae are shelf-packed left→right into bands (wrapping when
 * a band fills), mirroring computeLayout's genre packing, so the result is a
 * deterministic band/grid. The decorative scatter is seeded per constellation id.
 *
 * `albumSetHash` hashes the constellation ids + counts so StarMapScene can cache
 * the layout per (song set + mode), exactly like the genre layout's hash.
 */
export function computeNebulaLayout(constellations: GroupConstellation[]): NebulaLayout {
  // Hash the constellation SET (id + count, in input order) for the layout cache.
  const albumSetHash = fnv1a(
    constellations.map((c) => c.id + '=' + c.songCount).join(','),
  ).toString(16);

  // Size every nebula first (box = the glow's bounding square + label headroom),
  // preserving input order. Each nebula keeps a stable index for determinism.
  const sized = constellations.map((c, i) => {
    const r = nebulaRadius(c.songCount);
    return {
      c,
      i,
      r,
      w: r * 2,
      h: r * 2 + NEBULA_LABEL_H,
    };
  });

  // Band width grows with the count so many constellations stay roughly square.
  const bandWidth = Math.max(
    720,
    Math.ceil(Math.sqrt(Math.max(1, sized.length))) * (NEBULA_R_MAX + NEBULA_GAP) +
      SCENE_MARGIN * 2,
  );

  const nebulae: Nebula[] = [];
  let cursorX = SCENE_MARGIN;
  let bandTop = SCENE_MARGIN;
  let bandHeight = 0;
  let sceneRight = 0;

  for (const s of sized) {
    if (cursorX > SCENE_MARGIN && cursorX + s.w > bandWidth - SCENE_MARGIN) {
      // wrap to next band
      cursorX = SCENE_MARGIN;
      bandTop += bandHeight + NEBULA_GAP;
      bandHeight = 0;
    }
    const boxX = cursorX;
    const boxY = bandTop;
    // Center the glow within its box; the label sits in the headroom above it.
    const cx = boxX + s.r;
    const cy = boxY + NEBULA_LABEL_H + s.r;

    nebulae.push({
      id: s.c.id,
      label: s.c.label,
      songCount: s.c.songCount,
      x: cx,
      y: cy,
      r: s.r,
      filter: s.c.filter,
      stars: nebulaScatter(s.c.id, cx, cy, s.r),
    });

    cursorX += s.w + NEBULA_GAP;
    bandHeight = Math.max(bandHeight, s.h);
    sceneRight = Math.max(sceneRight, boxX + s.w);
  }

  const width = Math.max(bandWidth, sceneRight + SCENE_MARGIN);
  const height = bandTop + bandHeight + SCENE_MARGIN;

  return { nebulae, width, height, albumSetHash };
}
